import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { ROLES, ROLE_LABELS } from "@lagerlog/core";
import type { Grant, Role } from "@lagerlog/core";
import { team, untilDay, wordFor } from "../account.ts";
import type { Invite, MemberAccess, TeamMember } from "../account.ts";
import { useStore } from "../hooks.ts";
import { messageOf } from "./Login.tsx";
import { APP, IS_PANTRY } from "../brand.ts";

const shortDay = (at: number) => new Date(at).toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "short" });

type LinkBox = { title: string; url: string; expiresAt: number; share: string; name: string };
type Lager = { id: string; name: string };

/** What each role may (F20), as the owner reads it when choosing. */
const ROLE_HINTS: Record<Role, string> = {
  owner: "alles, auch Team, Rechte und neue Lager",
  manager: "in den freigegebenen Lagern alles außer Team und Rechte, mit Preisen",
  // VorratsBuch has no counting of its own (B1).
  staff: IS_PANTRY ? "in den freigegebenen Lagern einlagern und verbrauchen; ohne Preise" : "in den freigegebenen Lagern einlagern, entnehmen, zählen; ohne Preise",
  viewer: "Bestand der freigegebenen Lager sehen, ohne Preise",
};
const GRANT_LABELS: Record<Grant, string> = { book: "buchen", view: "nur sehen" };

/** The granted storages in one line: „Bar: buchen · Weinkeller: nur sehen“. */
function grantsText(lagers: Record<string, Grant>, all: Lager[]): string {
  const parts = all.filter((l) => lagers[l.id]).map((l) => `${l.name}: ${GRANT_LABELS[lagers[l.id]!]}`);
  return parts.length ? parts.join(" · ") : "kein Lager freigegeben";
}

/** "Betrieb" in the account page: who belongs to it; the owner invites, renames and manages. */
export function Team() {
  const { store } = useStore();
  const word = wordFor(store.config?.kind);
  const [data, setData] = useState<{ role: Role; members: TeamMember[]; invites: Invite[]; lagers?: Lager[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [box, setBox] = useState<LinkBox | null>(null);
  const [inviting, setInviting] = useState(false);
  /** The member or invitation whose role and storages are open for changing. */
  const [editing, setEditing] = useState<string | null>(null);

  async function load() {
    try {
      setData(await team.members());
    } catch (err) {
      setError(messageOf(err));
    }
  }
  useEffect(() => void load(), []);

  async function act(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(messageOf(err));
    }
    await load();
  }

  const inviteBox = (name: string, url: string, expiresAt: number): LinkBox => ({
    title: `Einladung für ${name}`,
    url,
    expiresAt,
    name,
    share: `Hallo ${name}, hier ist deine Einladung in ${word.the} „${store.config?.workspace}“ (${APP.short}). Mit dem Link legst du dein Konto an; er gilt bis ${untilDay(expiresAt)}, und nur einmal.`,
  });

  if (!data) return <section className="card">{error ? <p className="error-box">{error}</p> : <p className="muted">Lädt …</p>}</section>;
  const owner = data.role === "owner";
  const lagers = data.lagers ?? [];

  return (
    <section className="card team">
      <h2>{word.one}</h2>
      {owner ? <Rename /> : <p>{store.config?.workspace}</p>}
      <ul className="list">
        {data.members.map((m) => (
          <li key={m.id} className="member" data-testid={`member-${m.displayName}`}>
            <div className="row between">
              <div>
                <strong>{m.displayName}</strong> {m.me && <span className="muted">(du)</span>}
                {owner && (
                  <div className="muted small">
                    {m.username}
                    {!m.hasEmail && " · ohne E-Mail"}
                    {m.reset && <span className={m.reset.expiresAt <= Date.now() ? "error" : ""}> · Passwort zurückgesetzt, Link bis {shortDay(m.reset.expiresAt)}</span>}
                  </div>
                )}
              </div>
              <span className="side">{ROLE_LABELS[m.role]}</span>
            </div>
            {owner && m.lagers && <div className={`small ${Object.keys(m.lagers).length ? "muted" : "error"}`}>{grantsText(m.lagers, lagers)}</div>}
            {editing === m.id && (
              <AccessForm
                initial={{ role: m.role, lagers: m.lagers ?? {} }}
                lagers={lagers}
                name={m.displayName}
                onCancel={() => setEditing(null)}
                onSave={async (access) => {
                  await team.setAccess(m.id, access);
                  setEditing(null);
                  await load();
                }}
              />
            )}
            {owner && !m.me && editing !== m.id && (
              <div className="actions">
                <button onClick={() => setEditing(m.id)} data-testid="edit-access">
                  Rolle und Lager
                </button>
                <button
                  onClick={() =>
                    act(async () => {
                      const passkeys = m.passkeys ? ` Auch die Passkeys von ${m.displayName} gelten erst wieder, wenn ein neues Passwort gesetzt ist; dabei lassen sie sich entfernen.` : "";
                      if (!confirm(`Passwort von ${m.displayName} zurücksetzen? Das bisherige gilt sofort nicht mehr, und ${m.displayName} wird auf allen Geräten abgemeldet.${passkeys}`)) return;
                      const { url, expiresAt } = await team.resetLink(m.id);
                      setBox({ title: `Neues Passwort für ${m.displayName}`, url, expiresAt, name: m.displayName, share: `Hallo ${m.displayName}, mit diesem Link legst du ein neues Passwort fest (${APP.short}). Er gilt bis ${untilDay(expiresAt)}, und nur einmal.` });
                    })
                  }
                >
                  Passwort zurücksetzen
                </button>
                <button
                  className="danger"
                  onClick={() => {
                    if (confirm(`${m.displayName} aus ${word.the} nehmen? Was ${m.displayName} gebucht hat, bleibt im Journal.`)) void act(() => team.remove(m.id));
                  }}
                >
                  Entfernen
                </button>
              </div>
            )}
          </li>
        ))}
        {data.invites.map((i) => (
          <li key={i.id} className="member" data-testid={`invite-${i.name}`}>
            <div className="row between">
              <div>
                <strong>{i.name}</strong>
                <div className={`small ${i.expired ? "error" : "muted"}`}>eingeladen, {i.expired ? "Link abgelaufen" : `Link bis ${shortDay(i.expiresAt)}`}</div>
              </div>
              <span className="side">{ROLE_LABELS[i.role]}</span>
            </div>
            {i.role !== "owner" && <div className={`small ${Object.keys(i.lagers).length ? "muted" : "error"}`}>{grantsText(i.lagers, lagers)}</div>}
            {editing === i.id ? (
              <AccessForm
                initial={{ role: i.role, lagers: i.lagers }}
                lagers={lagers}
                name={i.name}
                onCancel={() => setEditing(null)}
                onSave={async (access) => {
                  await team.setInviteAccess(i.id, access);
                  setEditing(null);
                  await load();
                }}
              />
            ) : (
            <div className="actions">
              <button onClick={() => setEditing(i.id)}>Rolle und Lager</button>
              <button
                onClick={() =>
                  act(async () => {
                    const { invite, url } = await team.renew(i.id);
                    setBox(inviteBox(invite.name, url, invite.expiresAt));
                  })
                }
              >
                Neuer Link
              </button>
              <button className="danger" onClick={() => act(() => team.withdraw(i.id))}>
                Zurückziehen
              </button>
            </div>
            )}
          </li>
        ))}
      </ul>
      {box && <Box box={box} onClose={() => setBox(null)} />}
      {owner && !box && (
        <>
          {inviting ? (
            <InviteForm
              lagers={lagers}
              onCancel={() => setInviting(false)}
              onDone={async (name, access) => {
                const { invite, url } = await team.invite(name, access);
                setInviting(false);
                setBox(inviteBox(invite.name, url, invite.expiresAt));
                await load();
              }}
            />
          ) : (
            <button className="primary" onClick={() => setInviting(true)} data-testid="invite">
              Jemanden einladen
            </button>
          )}
          <p className="hint">Wer eingeladen ist, wählt Benutzername und Passwort selbst; das Passwort siehst du nie.</p>
        </>
      )}
      {error && <p className="error-box">{error}</p>}
    </section>
  );
}

/** The role as a list of full-width choices, and per storage: book, only see, or nothing. */
function AccessFields({ access, lagers, onChange }: { access: MemberAccess; lagers: Lager[]; onChange: (access: MemberAccess) => void }) {
  return (
    <>
      <fieldset className="choice vertical" data-testid="role">
        <legend>Rolle</legend>
        {ROLES.map((r) => (
          <label key={r} className={access.role === r ? "on" : ""}>
            <input type="radio" name="role" checked={access.role === r} onChange={() => onChange({ ...access, role: r })} data-testid={`role-${r}`} />
            <span>
              {ROLE_LABELS[r]}
              <span className="hint">{ROLE_HINTS[r]}</span>
            </span>
          </label>
        ))}
      </fieldset>
      {access.role === "owner" ? (
        <p className="hint">Inhaber sehen und buchen alle Lager.</p>
      ) : (
        <fieldset className="grants" data-testid="grants">
          <legend>Lager</legend>
          {lagers.length === 0 && <p className="hint">Es gibt noch keine Lager.</p>}
          {lagers.map((l) => (
            <label key={l.id} className="row between">
              <span>{l.name}</span>
              <select
                className="side"
                value={access.lagers[l.id] ?? ""}
                onChange={(e) => {
                  const next = { ...access.lagers };
                  if (e.target.value) next[l.id] = e.target.value as Grant;
                  else delete next[l.id];
                  onChange({ ...access, lagers: next });
                }}
                data-testid={`grant-${l.id}`}
              >
                <option value="">nichts</option>
                {access.role !== "viewer" && <option value="book">buchen</option>}
                <option value="view">nur sehen</option>
              </select>
            </label>
          ))}
          <p className="hint">Ein Lager ohne Freigabe sieht die Person nicht. Neue Lager sind zuerst nur für Inhaber frei.</p>
        </fieldset>
      )}
    </>
  );
}

/** With „Nur sehen“ nothing is booked: a storage to book in becomes one to see. */
const fitted = (access: MemberAccess): MemberAccess =>
  access.role === "viewer" ? { ...access, lagers: Object.fromEntries(Object.keys(access.lagers).map((l) => [l, "view" as Grant])) } : access;

function AccessForm({ initial, lagers, name, onSave, onCancel }: { initial: MemberAccess; lagers: Lager[]; name: string; onSave: (access: MemberAccess) => Promise<void>; onCancel: () => void }) {
  const [access, setAccess] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSave(access);
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form noValidate onSubmit={submit} className="access-form" data-testid="access-form">
      <AccessFields access={access} lagers={lagers} onChange={(a) => setAccess(fitted(a))} />
      {access.role !== "owner" && Object.keys(access.lagers).length === 0 && <p className="warn">Ohne Lager sieht {name} nichts.</p>}
      {error && <p className="error-box">{error}</p>}
      <div className="buttons">
        <button className="primary" disabled={busy} data-testid="access-save">
          Speichern
        </button>
        <button type="button" className="link" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

function InviteForm({ lagers, onDone, onCancel }: { lagers: Lager[]; onDone: (name: string, access: MemberAccess) => Promise<void>; onCancel: () => void }) {
  const [name, setName] = useState("");
  const [access, setAccess] = useState<MemberAccess>({ role: "staff", lagers: {} });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onDone(name.trim(), access);
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form noValidate onSubmit={submit}>
      <label>
        Name
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} autoFocus data-testid="invite-name" />
        <span className="hint">So heißt die Person bei euch; sie kann den Namen ändern.</span>
      </label>
      <AccessFields access={access} lagers={lagers} onChange={(a) => setAccess(fitted(a))} />
      {access.role !== "owner" && Object.keys(access.lagers).length === 0 && name.trim() && <p className="warn">Ohne Lager sieht {name.trim()} nichts.</p>}
      {error && <p className="error-box">{error}</p>}
      <div className="buttons">
        <button className="primary" disabled={busy || !name.trim()} data-testid="invite-create">
          Einladungslink erzeugen
        </button>
        <button type="button" className="link" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

function Box({ box, onClose }: { box: LinkBox; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard?.writeText(box.url).catch(() => undefined);
    setCopied(true);
  };
  return (
    <div className="linkbox" data-testid="link-box">
      <strong>{box.title}</strong>
      <input readOnly value={box.url} onFocus={(e) => e.target.select()} data-testid="link-url" />
      <div className="buttons">
        <button
          className="primary"
          onClick={async () => {
            if (navigator.share) await navigator.share({ text: `${box.share}\n${box.url}` }).catch(() => undefined);
            else await copy();
          }}
        >
          Link senden
        </button>
        <button onClick={copy}>{copied ? "Link kopiert" : "Link kopieren"}</button>
        <button className="link" onClick={onClose}>
          Fertig
        </button>
      </div>
      <p className="hint">
        Einmal verwendbar, gültig bis {untilDay(box.expiresAt)}. Schick ihn nur an {box.name}.
      </p>
    </div>
  );
}

function Rename() {
  const { store } = useStore();
  const current = store.config?.workspace ?? "";
  const [name, setName] = useState(current);
  const [state, setState] = useState<{ ok?: string; error?: string }>({});
  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      const { name: saved } = await team.rename(name);
      await store.renameWorkspace(saved);
      setState({ ok: "Umbenannt" });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  return (
    <form noValidate onSubmit={submit}>
      <label>
        Name
        <span className="row">
          <input value={name} onChange={(e) => [setName(e.target.value), setState({})]} maxLength={60} />
          <button disabled={name.trim() === current || !name.trim()}>Umbenennen</button>
        </span>
      </label>
      {state.ok && <p className="ok-text">{state.ok}</p>}
      {state.error && <p className="error-box">{state.error}</p>}
    </form>
  );
}
